import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  releaseConversation,
  type Lock,
} from '@/features/conversations/conversations.api';
import { conversationsKeys } from '@/features/conversations/keys';

interface ReleaseVariables {
  conversationId: string;
}

interface ReleaseMutationContext {
  previousLock: Lock | undefined;
}

/**
 * Release the caller's claim lock on a conversation.
 *
 *   - onMutate: clears the lock cache slot so the row's lock pill disappears
 *     immediately. Snapshots the prior value for rollback.
 *   - onError: restores the snapshot and toasts a quiet failure message.
 *   - onSettled: invalidates the lock cache slot regardless of outcome so
 *     the server's view (which may have changed independently) wins
 *     eventually.
 */
export function useReleaseConversation() {
  const queryClient = useQueryClient();

  return useMutation<
    { status: string },
    unknown,
    ReleaseVariables,
    ReleaseMutationContext
  >({
    mutationFn: ({ conversationId }) => releaseConversation(conversationId),

    onMutate: async ({ conversationId }) => {
      const lockKey = conversationsKeys.lock(conversationId);
      await queryClient.cancelQueries({ queryKey: lockKey });
      const previousLock = queryClient.getQueryData<Lock>(lockKey);
      // `setQueryData(key, undefined)` is a no-op in react-query; clear via
      // `removeQueries` so the row immediately repaints as unlocked.
      queryClient.removeQueries({ queryKey: lockKey, exact: true });
      return { previousLock };
    },

    onError: (_error, { conversationId }, context) => {
      const lockKey = conversationsKeys.lock(conversationId);
      if (context?.previousLock === undefined) {
        queryClient.removeQueries({ queryKey: lockKey, exact: true });
      } else {
        queryClient.setQueryData(lockKey, context.previousLock);
      }
      toast("Couldn't release — try again");
    },

    onSettled: (_data, _error, { conversationId }) => {
      void queryClient.invalidateQueries({
        queryKey: conversationsKeys.lock(conversationId),
      });
    },
  });
}
